import { useTrackEvent } from "kanopi-components/tracking";
import { useGlobal } from "kanopi-components/useGlobal";

import { useUser } from "../../auth/useUser";
import {
  getProductsFromRiskData,
  getRiskProfileId,
  replaceIdsWithStoragePaths,
  getCoverInformation,
  getEnabledCovers,
} from "../../util/tracking";

export function useTrackQuestionSetViewed({ flowType, ...args }: { flowType: string }) {
  const [data] = useGlobal("quoting");
  const _trackQuestionSetViewed = useTrackEvent({
    event: "Question Set Viewed",
    now: false,
    ...args,
  });
  return function trackQuestionSetViewed({
    questionSet,
    riskProfileId,
    riskData,
    initialValues = {},
    ...other
  }) {
    _trackQuestionSetViewed({
      properties: {
        enabledProducts: getProductsFromRiskData(riskData),
        riskProfileId: riskProfileId || getRiskProfileId(data),
        questionSetName: questionSet.key,
        questionSetId: questionSet.id,
        questionSetText: questionSet.description,
        questionSetOrder: questionSet.order,
        questionSetIsPrefilled: Object.values(initialValues).some(
          (val) => val !== undefined
        ),
        questionSetPrefill: replaceIdsWithStoragePaths(
          initialValues,
          questionSet?.questions
        ),
        // questions: questionSet?.questions, TODO: Find out what properties we need and flatten them
        flowType,
        ...other,
      },
    });
  };
}

export function useTrackQuestionSetCompleted({
  flowType,
  ...args
}: {
  flowType: string;
}) {
  const [data] = useGlobal("quoting");
  const _trackQuestionSetCompleted = useTrackEvent({
    event: "Question Set Completed",
    now: false,
    ...args,
  });
  return function trackQuestionSetCompleted({
    questionSet,
    riskProfileId,
    riskData,
    initialValues,
    answers,
    ...other
  }) {
    _trackQuestionSetCompleted({
      properties: {
        enabledProducts: getProductsFromRiskData(riskData),
        riskProfileId: riskProfileId || getRiskProfileId(data),
        questionSetName: questionSet.key,
        questionSetId: questionSet.id,
        questionSetText: questionSet.description,
        questionSetOrder: questionSet.order,
        questionSetIsPrefilled: !!Object.keys(initialValues).length,
        questionSetPrefill: replaceIdsWithStoragePaths(
          initialValues,
          questionSet?.questions
        ),
        // questions: questionSet?.questions, TODO: Find out what properties we need and flatten them
        questionSetAnswers: answers,
        flowType,
        ...other,
      },
    });
  };
}

export function useTrackCoverEditStarted({ ...args } = {}) {
  const _trackCoverEditStarted = useTrackEvent({
    event: "Cover Edit Started",
    now: false,
    ...args,
  });
  return function trackCoverEditStarted({ riskProfileId, riskData, cover, ...other }) {
    _trackCoverEditStarted({
      properties: {
        riskProfileId,
        ...getCoverInformation(cover, riskData),
        ...other,
      },
    });
  };
}

export function useTrackCoverAddStarted({ ...args } = {}) {
  const _trackCoverAddStarted = useTrackEvent({
    event: "Cover Add Started",
    now: false,
    ...args,
  });
  return function trackCoverAddStarted({ riskProfileId, riskData, cover, ...other }) {
    _trackCoverAddStarted({
      properties: {
        riskProfileId,
        ...getCoverInformation(cover, riskData),
        ...other,
      },
    });
  };
}

export function useTrackCoverAddCompleted({ ...args } = {}) {
  const [data] = useGlobal("quoting");
  const _trackCoverAddCompleted = useTrackEvent({
    event: "Cover Add Completed",
    now: false,
    ...args,
  });
  return function trackCoverAddCompleted({ riskProfileId, cover, ...other }) {
    _trackCoverAddCompleted({
      properties: {
        riskProfileId,
        ...getCoverInformation(cover, data?.riskProfile?.riskData),
        ...other,
      },
    });
  };
}

export function useTrackCoverRemoved({ ...args } = {}) {
  const _trackCoverRemoved = useTrackEvent({
    event: "Cover Removed",
    now: false,
    ...args,
  });
  return function trackCoverRemoved({ riskProfileId, cover, riskData, ...other }) {
    _trackCoverRemoved({
      properties: {
        riskProfileId,
        ...getCoverInformation(cover, riskData),
        ...other,
      },
    });
  };
}

export function useTrackReferralRequired({
  referralType,
  ...args
}: {
  referralType: string;
}) {
  const [data] = useGlobal("quoting");
  const _trackReferralRequired = useTrackEvent({
    event: "Referral Required",
    now: false,
    ...args,
  });
  return function trackReferralRequired({ referralReason, ...other }) {
    _trackReferralRequired({
      properties: {
        enabledProducts: getProductsFromRiskData(data?.riskProfile?.riskData),
        riskProfileId: getRiskProfileId(data),
        referralType,
        referralReason,
        ...other,
      },
    });
  };
}

export function useTrackQuoteStarted({ ...args } = {}) {
  const user = useUser();
  const _trackQuoteStarted = useTrackEvent({
    event: "Quote Started",
    now: false,
    ...args,
  });
  return function trackQuoteStarted({
    riskProfile,
    ...other
  }: {
    riskProfile: TRiskProfile;
  }) {
    _trackQuoteStarted({
      properties: {
        enabledProducts: getProductsFromRiskData(riskProfile?.riskData),
        riskProfileId: riskProfile?.id,
        user: {
          uuid: user.uuid,
          firstName: user.firstName,
          lastName: user.lastName,
        },
        distributionPartner: user?.latestReferrer?.key,
        ...other,
      },
    });
  };
}

export function useTrackQuoteViewed({ ...args } = {}) {
  const [data] = useGlobal("quoting");
  const user = useUser();
  const _trackQuoteViewed = useTrackEvent({
    event: "Quote Viewed",
    now: false,
    ...args,
  });
  return function trackQuoteViewed({
    quote,
    riskProfile,
    enabledCoverSections,
    ...other
  }: {
    quote;
    riskProfile: TRiskProfile;
    enabledCoverSections: TCoverSection[];
  }) {
    _trackQuoteViewed({
      properties: {
        enabledProducts: getProductsFromRiskData(data?.riskProfile?.riskData),
        enabledCovers: enabledCoverSections.map(({ title }) => title),
        riskProfileId: getRiskProfileId(data),
        currency: "AUD",
        distributionPartner: user?.latestReferrer?.key,
        pretaxPremium: quote?.preTaxPremiumSum,
        commission: quote?.commissionSum,
        total: riskProfile?.periodicPrice,
        ...other,
      },
    });
  };
}

export function useTrackQuoteDetailsUpdated({ ...args } = {}) {
  const _trackQuoteDetailsUpdated = useTrackEvent({
    event: "Quote Details Updated",
    now: false,
    ...args,
  });
  return function trackQuoteDetailsUpdated({
    riskProfile,
    ...other
  }: {
    riskProfile: TRiskProfile;
  }) {
    _trackQuoteDetailsUpdated({
      properties: {
        riskProfileId: riskProfile?.id,
        ...other,
      },
    });
  };
}

export function useTrackCheckoutStarted({ ...args } = {}) {
  const [data] = useGlobal("quoting");
  const user = useUser();
  const _trackCheckoutStarted = useTrackEvent({
    event: "Checkout Started",
    now: false,
    ...args,
  });
  return function trackCheckoutStarted({
    riskProfile,
    quote,
    ...other
  }: {
    riskProfile: TRiskProfile;
    quote?: { preTaxPremiumSum?: number; commissionSum?: number };
  }) {
    _trackCheckoutStarted({
      properties: {
        riskProfileId: riskProfile?.id,
        distributionPartner: user?.latestReferrer,
        currency: "AUD",
        covers: getEnabledCovers(data),
        pretaxPremium: quote?.preTaxPremiumSum,
        commission: quote?.commissionSum,
        total: riskProfile?.periodicPrice,
        ...other,
      },
    });
  };
}

export function useTrackCheckoutStepViewed({
  flowType,
  ...args
}: {
  flowType: string;
}) {
  const _trackCheckoutStepViewed = useTrackEvent({
    event: "Checkout Step Viewed",
    now: false,
    ...args,
  });
  return function trackCheckoutStepViewed({
    riskProfileId,
    step,
    quote,
    ...other
  }: {
    riskProfileId: TRiskProfile["id"];
    step: number;
    quote?: { preTaxPremiumSum?: number; commissionSum?: number };
  }) {
    _trackCheckoutStepViewed({
      properties: {
        type: flowType,
        riskProfileId,
        step,
        pretaxPremium: quote?.preTaxPremiumSum,
        commission: quote?.commissionSum,
        ...other,
      },
    });
  };
}

export function useTrackCheckoutStepCompleted({
  flowType,
  ...args
}: {
  flowType: string;
}) {
  const _trackCheckoutStepCompleted = useTrackEvent({
    event: "Checkout Step Completed",
    now: false,
    ...args,
  });
  return function trackCheckoutStepCompleted({
    riskProfileId,
    step,
    quote,
    ...other
  }: {
    riskProfileId: TRiskProfile["id"];
    step: number;
    quote?: { preTaxPremiumSum?: number; commissionSum?: number };
  }) {
    _trackCheckoutStepCompleted({
      properties: {
        type: flowType,
        riskProfileId,
        step,
        pretaxPremium: quote?.preTaxPremiumSum,
        commission: quote?.commissionSum,
        ...other,
      },
    });
  };
}

export function useTrackPaymentInfoEntered({ ...args }: object = {}) {
  const _trackPaymentInfoEntered = useTrackEvent({
    event: "Payment Info Entered",
    now: false,
    ...args,
  });
  return function trackPaymentInfoEntered({
    riskProfileId,
    step,
    quote,
    ...other
  }: {
    riskProfileId: TRiskProfile["id"];
    step: number;
    quote?: { preTaxPremiumSum?: number; commissionSum?: number };
  }) {
    _trackPaymentInfoEntered({
      properties: {
        riskProfileId,
        step,
        pretaxPremium: quote?.preTaxPremiumSum,
        commission: quote?.commissionSum,
        ...other,
      },
    });
  };
}

export function useTrackOrderCompleted({ ...args }: object = {}) {
  const user = useUser();
  const _trackOrderCompleted = useTrackEvent({
    event: "Order Completed",
    now: false,
    ...args,
  });
  return function trackOrderCompleted({
    riskProfile,
    quote,
    ...other
  }: {
    riskProfile: TRiskProfile;
    quote?: { preTaxPremiumSum?: number; commissionSum?: number };
  }) {
    _trackOrderCompleted({
      properties: {
        riskProfileId: riskProfile?.id,
        distributionPartner: user?.latestReferrer,
        currency: "AUD",
        total: riskProfile?.periodicPrice,
        pretaxPremium: quote?.preTaxPremiumSum,
        commission: quote?.commissionSum,
        ...other,
      },
    });
  };
}
